import type { RDMDOpts } from '@readme/iso';

import React, { useContext } from 'react';

import { ProjectContext } from '@core/context';
import useEnvInfo from '@core/hooks/useEnvInfo';
import { useSuperHubStore } from '@core/store';

import AIDropdown from '@routes/SuperHub/components/AIDropdown';

import RDMD, { TOC } from '@ui/RDMD';

import MigrationError from './MigrationError';

interface DocBodyProps {
  dehydrated?: { body: string; toc?: string; css?: string };
  docBody: string;
  editUrl: string;
  footer: React.ReactNode;
  hasMdxError: boolean;
  hideTOC: boolean;
  isAdmin?: boolean;
  mdx: boolean;
  opts: RDMDOpts;
  showAIDropdown?: boolean;
}

const DocBody = ({
  isAdmin,
  hideTOC,
  dehydrated,
  docBody,
  editUrl,
  mdx,
  opts = {},
  footer,
  hasMdxError = false,
  showAIDropdown = false,
}: DocBodyProps) => {
  const { isClient } = useEnvInfo();
  const { project } = useContext(ProjectContext);
  const isOwlbotOpenCached = useSuperHubStore(s => s.isOwlbotOpenCached);
  const isMdx = !hasMdxError && mdx;

  // Check if OwlBot is enabled and open - if so, hide TOC and AIDropdown
  const isOwlbotEnabledAndOpen = project?.owlbot?.enabled && project?.owlbot?.newExperience && isOwlbotOpenCached;

  return (
    <div className="grid-container-fluid" id="content-container">
      <section
        className={`content-body grid-${hideTOC ? 100 : 75} ${isOwlbotEnabledAndOpen ? 'expand-on-small-screens' : ''}`}
      >
        {!!hasMdxError && !!editUrl && !!isAdmin && <MigrationError editUrl={editUrl} />}
        <RDMD
          key={isClient ? window.location.pathname : null}
          body={docBody}
          className="rm-Markdown markdown-body ng-non-bindable"
          css={dehydrated?.css}
          dehydrated={dehydrated?.body}
          format={isMdx ? 'mdx' : 'md'}
          opts={opts}
          style={{ marginLeft: 31 }}
        />
        {!!footer && footer}
      </section>
      <section className={`content-toc grid-25 ${isOwlbotEnabledAndOpen ? 'hide-on-small-screens' : ''}`}>
        {/* Use the enhanced AIDropdown that manages its own visibility */}
        {/* Hide AIDropdown when OwlBot is enabled and open */}
        <AIDropdown showAIDropdown={showAIDropdown} />

        {/* Hide TOC when OwlBot is enabled and open (but not on screens wider than 1500px) */}
        {!hideTOC && <TOC body={docBody} dehydrated={dehydrated?.toc} format={isMdx ? 'mdx' : 'md'} opts={opts} />}
      </section>
    </div>
  );
};

export default DocBody;
